/**
 * OPS-11: Operational Timeline — Chronological Event Feed
 *
 * Shows recent operational events synthesized from alerts, guest risks,
 * and shift briefing highlights. Provides temporal context for the
 * current operational state.
 *
 * Limit: 12 most recent items, displayed in reverse chronological order.
 */

import { useMemo } from 'react';
import { InfoTooltip } from './InfoTooltip';
import type {
  OperationalAlert,
  GuestExperienceRisk,
  ShiftHighlight,
  AlertSeverity,
} from '../../services/controlTowerTypes';
import { getServiceImpact, getImpactStyle, type ServiceImpact } from './serviceImpactTags';

// ─── Timeline Event Model ────────────────────────────────────────────────────

interface TimelineEvent {
  id: string;
  timestamp: number;
  title: string;
  description: string;
  severity: AlertSeverity;
  category: 'alert' | 'risk' | 'highlight';
  icon: string;
  /** Whether this event corresponds to a currently active alert or risk */
  isActive: boolean;
  /** Source alert/risk ID for matching */
  sourceId: string;
  /** Service impact category */
  impact: ServiceImpact;
}

const MAX_EVENTS = 12;

const SEVERITY_COLORS: Record<AlertSeverity, { dot: string; line: string; text: string }> = {
  critical: { dot: 'bg-rose-400 shadow-rose-400/50', line: 'via-rose-400/30', text: 'text-rose-400' },
  warning:  { dot: 'bg-amber-400 shadow-amber-400/50', line: 'via-amber-400/30', text: 'text-amber-400' },
  info:     { dot: 'bg-sky-400 shadow-sky-400/50', line: 'via-sky-400/30', text: 'text-sky-400' },
};

const CATEGORY_ICONS: Record<string, string> = {
  HOUSEKEEPING: '🏨',
  MAINTENANCE: '🔧',
  REQUEST: '🛎️',
  SERVICE: '🛎️',
  RESPONSE: '⏱️',
  SLA: '📋',
  WORKLOAD: '⚡',
  CAPACITY: '📊',
  GUEST: '👤',
  SPIKE: '📈',
  DEFAULT_ALERT: '🔔',
  DEFAULT_RISK: '⚠️',
  DEFAULT_HIGHLIGHT: '📌',
};

const FALLBACK_ICON = '🔔';

function iconForType(type: string, category: TimelineEvent['category']): string {
  const t = (type ?? '').toUpperCase();
  for (const [key, icon] of Object.entries(CATEGORY_ICONS)) {
    if (key !== 'DEFAULT_ALERT' && key !== 'DEFAULT_RISK' && key !== 'DEFAULT_HIGHLIGHT' && t.includes(key)) {
      return icon;
    }
  }
  const defaultKey = category === 'alert' ? 'DEFAULT_ALERT'
    : category === 'risk' ? 'DEFAULT_RISK'
    : 'DEFAULT_HIGHLIGHT';
  return CATEGORY_ICONS[defaultKey] ?? FALLBACK_ICON;
}

function formatTime(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDuration(ms: number): string {
  const totalMin = Math.max(1, Math.round(ms / 60_000));
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// ─── Event Synthesis ─────────────────────────────────────────────────────────

function synthesizeEvents(
  alerts: OperationalAlert[],
  risks: GuestExperienceRisk[],
  highlights: ShiftHighlight[],
  referenceTimeMs: number,
): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  // Active sets for matching
  const activeAlertIds = new Set(alerts.map((a) => a.id));
  const activeRiskTypes = new Set(risks.map((r) => r.type));

  // Alerts have real timestamps
  for (const alert of alerts) {
    events.push({
      id: `alert-${alert.id}`,
      timestamp: alert.detectedAt,
      title: alert.title,
      description: alert.explanation,
      severity: alert.severity,
      category: 'alert',
      icon: iconForType(alert.type, 'alert'),
      isActive: activeAlertIds.has(alert.id),
      sourceId: alert.id,
      impact: getServiceImpact(alert.type),
    });
  }

  // Risks don't have individual timestamps — stagger them before reference time
  for (const [i, risk] of risks.entries()) {
    events.push({
      id: `risk-${risk.type}-${i}`,
      timestamp: referenceTimeMs - (i + 1) * 60_000, // stagger 1 min apart
      title: risk.explanation,
      description: risk.explanation,
      severity: risk.severity,
      category: 'risk',
      icon: iconForType(risk.type, 'risk'),
      isActive: activeRiskTypes.has(risk.type),
      sourceId: risk.type,
      impact: getServiceImpact(risk.type),
    });
  }

  // Highlights → stagger earlier
  for (const [i, h] of highlights.entries()) {
    events.push({
      id: `highlight-${h.type}-${i}`,
      timestamp: referenceTimeMs - (risks.length + i + 1) * 60_000,
      title: h.type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()),
      description: h.message,
      severity: h.severity ?? 'info',
      category: 'highlight',
      icon: iconForType(h.type, 'highlight'),
      isActive: false,
      sourceId: h.type,
      impact: getServiceImpact(h.type),
    });
  }

  // Sort newest first, trim
  events.sort((a, b) => b.timestamp - a.timestamp);
  return events.slice(0, MAX_EVENTS);
}

// ─── Component ───────────────────────────────────────────────────────────────

interface OperationalTimelineProps {
  alerts: OperationalAlert[];
  risks: GuestExperienceRisk[];
  highlights: ShiftHighlight[];
  referenceTimeMs: number;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function OperationalTimeline({
  alerts,
  risks,
  highlights,
  referenceTimeMs,
  collapsed,
  onToggleCollapse,
}: OperationalTimelineProps) {
  const events = useMemo(
    () => synthesizeEvents(alerts, risks, highlights, referenceTimeMs),
    [alerts, risks, highlights, referenceTimeMs],
  );

  if (events.length === 0) {
    return (
      <div className="rounded-2xl bg-slate-800/50 border border-slate-700/50 p-5 shadow-lg">
        <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-1.5 mb-4">
          Operational Timeline
          <InfoTooltip text="The operational timeline shows the chronological sequence of alerts, guest risks, and shift events — providing a living audit trail of hotel operations." />
        </h2>
        <div className="text-center py-6">
          <p className="text-slate-500 text-sm">No recent operational events</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-slate-800/50 border border-slate-700/50 p-5 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
          Operational Timeline
          <InfoTooltip text="The operational timeline shows the chronological sequence of alerts, guest risks, and shift events — providing a living audit trail of hotel operations." />
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-slate-500">
            {events.length} event{events.length !== 1 ? 's' : ''}
          </span>
          {onToggleCollapse && (
            <button
              type="button"
              onClick={onToggleCollapse}
              aria-label={collapsed ? 'Expand timeline' : 'Collapse timeline'}
              className="text-xs text-slate-500 hover:text-slate-300 transition-colors rounded px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              {collapsed ? '▸ Expand' : '▾ Collapse'}
            </button>
          )}
        </div>
      </div>

      {!collapsed && (
      <div className="relative max-h-96 overflow-y-auto pr-1">
        {/* Vertical line */}
        <div className="absolute left-3 top-0 bottom-0 w-px bg-gradient-to-b from-slate-600/50 via-slate-700/30 to-transparent" />

        <div className="space-y-0.5">
          {events.map((event, idx) => {
            const colors = SEVERITY_COLORS[event.severity] ?? SEVERITY_COLORS.info;
            const isFirst = idx === 0;
            const now = Date.now();
            const elapsed = now - event.timestamp;
            const showDuration = event.category !== 'highlight' && elapsed > 0;

            return (
              <div key={event.id} className={`relative flex items-start gap-3 pl-7 py-1 group ${
                event.isActive ? 'rounded-lg ring-1 ring-current/10' : ''
              }`}>
                {/* Timeline dot — pulsing for active incidents */}
                <div
                  className={`absolute left-1.5 top-3.5 w-3 h-3 rounded-full ${colors.dot} ${
                    isFirst ? 'shadow-lg ring-2 ring-current/20' : ''
                  }`}
                  role="img"
                  aria-label={`${event.severity} ${event.category}${event.isActive ? ' — active' : ''}`}
                >
                  {event.isActive && (
                    <span className="absolute inset-0 rounded-full animate-ping opacity-50" style={{ backgroundColor: 'inherit' }} />
                  )}
                </div>

                {/* Timestamp */}
                <div className="flex-shrink-0 w-12 pt-0.5">
                  <span className="text-[11px] text-slate-500 font-mono tabular-nums">
                    {formatTime(event.timestamp)}
                  </span>
                </div>

                {/* Content */}
                <div className={`flex-1 min-w-0 rounded-lg px-3 py-2 transition-colors ${
                  event.isActive
                    ? 'bg-slate-900/50 border border-slate-600/40'
                    : 'bg-slate-900/30 group-hover:bg-slate-900/50'
                }`}>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm">{event.icon}</span>
                    <span className="text-xs font-medium text-slate-300 truncate">{event.title}</span>
                    {event.isActive && (
                      <span className="flex-shrink-0 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 tracking-wider">
                        Active
                      </span>
                    )}
                    <span className={`flex-shrink-0 text-[9px] font-bold uppercase px-1 py-0.5 rounded ${
                      event.severity === 'critical' ? 'bg-rose-500/20 text-rose-400' :
                      event.severity === 'warning' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-sky-500/20 text-sky-400'
                    }`}>
                      {event.category}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 line-clamp-1">{event.description}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {(() => { const s = getImpactStyle(event.impact); return (
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${s.bg} ${s.text}`}>
                        {event.impact}
                      </span>
                    ); })()}
                    {showDuration && (
                      <span className="text-[10px] text-slate-600 font-mono tabular-nums">
                        ⏱ {formatDuration(elapsed)} ago
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
}
